/**
 * Pricing catalog.
 *
 * Turns the abstract "credits" currency into a concrete, per-feature, per-plan
 * outcome that gets baked straight into each plan card ("15 tech packs / month,
 * + Excel export, + 2 sketch-edits"). The page leads with whichever feature the
 * visitor arrived from. Highlights are authored as ADDITIVE per tier (only
 * what's new vs the tier below) so the cards read Botika-style:
 * "Everything in Pro, plus …".
 */

// Monthly / annual credit allowance per plan. Mirrors utils/plans.js + the
// pricing-margin workbook.
export const PLAN_CREDITS = {
  free: 25,
  standard: 125,
  pro: 375,
  studio: 1000,
  standard_annual: 1500,
  pro_annual: 4500,
  studio_annual: 12000,
};

// The plan columns, in order. Prices are NUMBERS (formatted at render time).
// Annual is billed once a year at a 17% discount; the page shows the
// monthly-EQUIVALENT price (priceYr / 12) so visitors always compare a per-month
// number. Credits/month are identical monthly vs annual (annual just discounts
// the price), so the per-feature outcome counts never change with the toggle.
// Enterprise is contact-only (no self-serve price or credits).
export const PLAN_TIERS = [
  { key: "standard", name: "Standard", tagline: "Great for getting started", priceMo: 29, priceYr: 290, creditsMo: 125 },
  { key: "pro", name: "Pro", tagline: "For professional designers", priceMo: 69, priceYr: 690, creditsMo: 375, popular: true, dark: true },
  { key: "studio", name: "Studio", tagline: "For studios & teams", priceMo: 149, priceYr: 1490, creditsMo: 1000 },
  { key: "enterprise", name: "Enterprise", tagline: "For scale & custom needs", enterprise: true },
];

export const DISPLAY_TIERS = PLAN_TIERS.map((t) => t.key);

// Calendly link for "Book a meeting" — same one the home page uses.
export const ENTERPRISE_CONTACT_URL = "https://calendly.com/adstronaut/15min";

// Platform-wide perks (NOT per-feature counts). Authored ADDITIVE per tier so
// the strip under the cards reads "Everything in Pro, plus …". Rendered below
// the pricing grid, aligned with the four columns.
export const PLATFORM_BENEFITS = {
  standard: { items: ["Every image in HD", "No Adstronaut signature on images"] },
  pro: { prev: "Standard", items: ["2K resolution", "2 free edits per generation", "Excel exports"] },
  studio: { prev: "Pro", items: ["5 AI sketch-edits per sketch", "Batch processing — coming soon"] },
};

// The Enterprise card body — "Everything in Studio, plus" these.
export const ENTERPRISE_PERKS = [
  "Custom credit volume each month",
  "Customer success manager",
  "Platform security & privacy",
  "Role management & access control",
  "Admin dashboard with AI analytics",
  "In-person / online onboarding workshops",
  "Batch processing & API access",
  "Custom workflows",
  "Tailor-made image models with your brand DNA",
];

// ---------------------------------------------------------------------------
// Feature catalog. Order = default ordering (Tech Pack anchors, then Photoshoot).
//   creditCost   credits per billable unit (drives the quantity math)
//   unit/units   noun for the outcome ("tech pack" / "tech packs")
//   highlights   ADDITIVE per tier — only what that tier adds over the one below
// ---------------------------------------------------------------------------
export const PRICING_FEATURES = [
  {
    id: "tech-pack",
    name: "Tech Pack Generator",
    route: "/tech-pack",
    landing: "/tech-pack-generator",
    creditCost: 25,
    unit: "tech pack",
    units: "tech packs",
    highlights: {
      free: ["Core sections only — premium sections blurred", "Watermarked PDF"],
      standard: ["Every section unlocked", "Clean PDF, no watermark"],
      pro: ["Editable Excel export", "2 AI sketch-edits per sketch"],
      studio: ["5 AI sketch-edits per sketch"],
    },
  },
  {
    id: "ai-photoshoot",
    name: "AI Photoshoots",
    route: "/photoshoots",
    landing: "/ai-photoshoots",
    creditCost: 5,
    unit: "on-model shot",
    units: "on-model shots",
    highlights: {
      free: ["2 photos included", "Every model & background", "1K · small Adstronaut signature"],
      standard: ["Up to 20 images · unlimited poses", "No signature"],
      pro: ["2K resolution"],
      studio: [],
    },
  },
  {
    id: "lookbook",
    name: "Lookbook Creator",
    route: "/product-view",
    landing: "/product-view-generator",
    creditCost: 5,
    unit: "view",
    units: "lookbook views",
    highlights: {
      free: ["2 views included", "1K · small Adstronaut signature"],
      standard: ["Batch up to 10 products", "No signature"],
      pro: ["2K resolution"],
      studio: [],
    },
  },
  {
    id: "ai-designer",
    name: "AI Designer",
    route: "/moodboards",
    landing: "/ai-designer",
    creditCost: 10, // finished concept = 4-illustration grid (5) + photoreal render (5)
    unit: "finished concept",
    units: "finished concepts",
    highlights: {
      free: ["1 round of 4 ideas + 1 photoreal render", "2K · small Adstronaut signature on renders"],
      standard: ["Unlimited concept grids & renders", "No signature"],
      pro: [],
      studio: [],
    },
  },
  {
    id: "design-variations",
    name: "Design Variations",
    route: "/variations",
    landing: "/design-variations",
    creditCost: 2,
    unit: "variation",
    units: "variations",
    highlights: {
      free: ["2 variations included", "1K · small Adstronaut signature"],
      standard: ["No signature"],
      pro: ["2K resolution"],
      studio: [],
    },
  },
  {
    id: "color-changer",
    name: "Color Changer",
    route: "/color-changer",
    landing: "/garment-recolor",
    creditCost: 2,
    unit: "colorway",
    units: "colorways",
    highlights: {
      free: ["3 colorways included", "1 image per batch", "1K · small Adstronaut signature"],
      standard: ["Batch up to 5 images", "No signature"],
      pro: ["Batch up to 10 images", "2K resolution"],
      studio: [],
    },
  },
  {
    id: "refabric",
    name: "Fabric Swapper",
    route: "/refabric",
    landing: "/fabric-swapper",
    creditCost: 2,
    unit: "swap",
    units: "fabric swaps",
    highlights: {
      free: ["2 swaps included", "21 presets + custom swatch uploads", "1K · small Adstronaut signature"],
      standard: ["No signature"],
      pro: ["2K resolution"],
      studio: [],
    },
  },
  {
    id: "product-editor",
    name: "Product Editor",
    route: "/editor",
    landing: "/garment-editor",
    creditCost: 2,
    unit: "edit",
    units: "edits",
    highlights: {
      free: ["2 edits included", "1K · small Adstronaut signature"],
      standard: ["Unlimited edits, every version saved", "No signature"],
      pro: ["2K resolution"],
      studio: [],
    },
  },
  {
    id: "product-tagger",
    name: "Product Tagger",
    route: "/catalog",
    landing: "/product-tagger",
    creditCost: 0.2,
    unit: "product",
    units: "products tagged",
    highlights: {
      free: ["10 products included, in one batch"],
      standard: ["Batch up to 50 products", "13 attributes per SKU"],
      pro: [],
      studio: [],
    },
  },
];

const FEATURE_BY_ID = Object.fromEntries(PRICING_FEATURES.map((f) => [f.id, f]));
const FEATURE_BY_ROUTE = {};
for (const f of PRICING_FEATURES) {
  if (f.route) FEATURE_BY_ROUTE[f.route] = f.id;
  if (f.landing) FEATURE_BY_ROUTE[f.landing] = f.id;
}

export const getFeature = (id) => FEATURE_BY_ID[id] || null;

/** Map a URL pathname (feature route OR landing route) → feature id. */
export function featureIdForPath(pathname) {
  if (!pathname) return null;
  if (FEATURE_BY_ROUTE[pathname]) return FEATURE_BY_ROUTE[pathname];
  const first = pathname.split("/").filter(Boolean)[0];
  return first ? FEATURE_BY_ROUTE["/" + first] || null : null;
}

/** Which feature the pricing page leads with: ?from= → sessionStorage → default. */
export const DEFAULT_FEATURE_ID = "tech-pack";
export function resolveFeatureContext(...candidates) {
  for (const c of candidates) {
    if (!c) continue;
    if (FEATURE_BY_ID[c]) return c;
    if (FEATURE_BY_ROUTE[c]) return FEATURE_BY_ROUTE[c];
  }
  return DEFAULT_FEATURE_ID;
}

/** The per-card outcome for a feature on a plan tier. */
export function getCardOutcome(featureId, tierKey) {
  const f = getFeature(featureId) || PRICING_FEATURES[0];
  const credits = PLAN_CREDITS[tierKey] ?? 0;
  return {
    feature: f,
    quantity: f.creditCost ? Math.floor(credits / f.creditCost) : null,
    highlights: f.highlights[tierKey] || [],
  };
}

/** A few other tools' quantities on a plan — for the "mix any tools" line. */
export function getAllQuantities(tierKey) {
  const credits = PLAN_CREDITS[tierKey] ?? 0;
  return PRICING_FEATURES.map((f) => ({
    id: f.id,
    units: f.units,
    quantity: f.creditCost ? Math.floor(credits / f.creditCost) : null,
  }));
}

// Short, obvious outcome labels for the per-plan feature list (every plan card
// lists ALL of these with its own count).
export const FEATURE_LABELS = {
  "tech-pack": "Tech Packs",
  "ai-photoshoot": "Photoshoots",
  "lookbook": "Lookbook Views",
  "ai-designer": "Design Generations",
  "design-variations": "Variation Generations",
  "color-changer": "Product Recolors",
  "refabric": "Fabric Swaps",
  "product-editor": "Product Edits",
  "product-tagger": "Product Tags",
};

/**
 * The full feature list for one plan tier, as {id, label, quantity}. Static
 * content — every plan shows every feature. The only nod to context is
 * ordering: the feature the visitor arrived from floats to the top so a subtle
 * highlighter starts there. Order is identical across tiers so a highlight band
 * lines up across all four cards.
 */
export function getFeatureRows(tierKey, contextId) {
  const credits = PLAN_CREDITS[tierKey] ?? 0;
  const ordered = [
    ...PRICING_FEATURES.filter((f) => f.id === contextId),
    ...PRICING_FEATURES.filter((f) => f.id !== contextId),
  ];
  return ordered.map((f) => ({
    id: f.id,
    label: FEATURE_LABELS[f.id] || f.name,
    quantity: f.creditCost ? Math.floor(credits / f.creditCost) : null,
  }));
}
